"use client";

import { useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import { niceTicks } from "@/components/tools/stats-tests/interaction-plot";
import { downloadSvg } from "@/lib/tool-kit/download";
import { CHART_INK, seriesColor } from "@/lib/tool-kit/palette";
import type { NumberFormatter } from "@/lib/tool-kit/stats-tests/format";

/*
 * A normal Q–Q plot: each ordered value against the normal score it would have in a
 * perfectly normal sample, with a reference line through the first and third quartiles
 * (R's `qqline`). The quartile line is not dragged by an outlier the way x̄ + s·z is, so
 * the bulk of normal-looking data hugs it and a stray point stands clear of it. A bow
 * means skew, an S-shape heavy or light tails.
 */

const WIDTH = 520;
const HEIGHT = 360;
const MARGIN = { top: 20, right: 24, bottom: 56, left: 72 };
const PLOT_W = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_H = HEIGHT - MARGIN.top - MARGIN.bottom;

/** Φ⁻¹(0.75): the normal score of the upper quartile. */
const Z_Q3 = 0.674_489_750_196_081_7;

export function QqPlot({
  points,
  q1,
  q3,
  xTitle,
  yTitle,
  label,
  downloadLabel,
  fmt,
}: {
  points: readonly { theoretical: number; sample: number }[];
  /** The sample's first and third quartiles. */
  q1: number;
  q3: number;
  xTitle: string;
  yTitle: string;
  label: string;
  downloadLabel: string;
  fmt: NumberFormatter;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const xs = points.map((p) => p.theoretical);
  const ys = points.map((p) => p.sample);
  const xTicks = niceTicks(Math.min(...xs), Math.max(...xs));
  const slope = (q3 - q1) / (2 * Z_Q3);
  const centre = (q1 + q3) / 2;
  const lineEnds = [xTicks[0], xTicks.at(-1) as number].map(
    (z) => centre + slope * z
  );
  const yTicks = niceTicks(
    Math.min(...ys, ...lineEnds),
    Math.max(...ys, ...lineEnds)
  );
  const [xMin, xMax] = [xTicks[0], xTicks.at(-1) as number];
  const [yMin, yMax] = [yTicks[0], yTicks.at(-1) as number];
  const x = (v: number) =>
    MARGIN.left + ((v - xMin) / (xMax - xMin || 1)) * PLOT_W;
  const y = (v: number) =>
    MARGIN.top + PLOT_H - ((v - yMin) / (yMax - yMin || 1)) * PLOT_H;
  return (
    <div className="space-y-2">
      <svg
        aria-label={label}
        className="h-auto w-full max-w-xl rounded-lg border border-border"
        ref={ref}
        role="img"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect fill={CHART_INK.background} height={HEIGHT} width={WIDTH} />
        {yTicks.map((tick) => (
          <g key={`y-${tick}`}>
            <line
              stroke={CHART_INK.grid}
              x1={MARGIN.left}
              x2={MARGIN.left + PLOT_W}
              y1={y(tick)}
              y2={y(tick)}
            />
            <text
              dominantBaseline="middle"
              fill={CHART_INK.muted}
              fontSize="12"
              textAnchor="end"
              x={MARGIN.left - 8}
              y={y(tick)}
            >
              {fmt(tick)}
            </text>
          </g>
        ))}
        {xTicks.map((tick) => (
          <g key={`x-${tick}`}>
            <line
              stroke={CHART_INK.grid}
              x1={x(tick)}
              x2={x(tick)}
              y1={MARGIN.top}
              y2={MARGIN.top + PLOT_H}
            />
            <text
              fill={CHART_INK.muted}
              fontSize="12"
              textAnchor="middle"
              x={x(tick)}
              y={MARGIN.top + PLOT_H + 18}
            >
              {fmt(tick)}
            </text>
          </g>
        ))}
        <line
          stroke={seriesColor(3)}
          strokeDasharray="6 4"
          strokeWidth="2"
          x1={x(xMin)}
          x2={x(xMax)}
          y1={y(lineEnds[0])}
          y2={y(lineEnds[1])}
        />
        {points.map((point, i) => (
          <circle
            cx={x(point.theoretical)}
            cy={y(point.sample)}
            fill={seriesColor(0)}
            fillOpacity="0.85"
            // Points are positional: equal values can share coordinates.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional points
            key={i}
            r="4"
          />
        ))}
        <text
          fill={CHART_INK.text}
          fontSize="13"
          textAnchor="middle"
          x={MARGIN.left + PLOT_W / 2}
          y={HEIGHT - 12}
        >
          {xTitle}
        </text>
        <text
          fill={CHART_INK.text}
          fontSize="13"
          textAnchor="middle"
          transform={`translate(18 ${MARGIN.top + PLOT_H / 2}) rotate(-90)`}
        >
          {yTitle}
        </text>
      </svg>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadSvg("normal-qq-plot.svg", ref.current);
          }
        }}
        type="button"
      >
        {downloadLabel}
      </button>
    </div>
  );
}
